/**
 * Shared persistence for the three prize-display toggles — Contestants /
 * Results / Live (`display_prize_to_contestants`,
 * `show_prize_amounts_in_results`, `show_prize_amounts_before_complete`).
 *
 * Lifted out of EventSection (per-event) and RodeoListItem (rodeo-wide master)
 * so the Prize Management tab can drive the SAME switches without becoming a
 * third copy of the dependency invariant below. The switch components
 * (EventPrizeDisplayToggles, RodeoMasterPrizeToggles) were already pure and
 * controlled; only the save half needed extracting.
 *
 * THE INVARIANT, in one place: `show_prize_amounts_before_complete` (Live) is
 * meaningless without `show_prize_amounts_in_results` (Results). Turning
 * Results off cascades Live off, and Live is never persisted true while Results
 * is false. Three copies of that rule is three chances to write a row the rest
 * of the app can't interpret.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUpdateEvents,
  eventsKeys,
} from '@/api/generated_hooks/generated-events-hooks';
import type { EventPrizeDisplayTogglesValues } from '@/components/delegate/EventPrizeDisplayToggles';
import type { PrizeToggleField } from '@/components/delegate/RodeoMasterPrizeToggles';

/** The persisted shape of one event's three toggles. */
interface ToggleState {
  display: boolean;
  results: boolean;
  before: boolean;
}

/** Read an event row's effective toggle state. Results defaults ON. */
function readToggles(event: any): ToggleState {
  return {
    display: !!event?.display_prize_to_contestants,
    results: event?.show_prize_amounts_in_results !== false,
    before: !!event?.show_prize_amounts_before_complete,
  };
}

/** Apply one master field to a state, cascading the Results → Live invariant. */
function applyField(
  cur: ToggleState,
  field: PrizeToggleField,
  target: boolean,
): ToggleState {
  const next = { ...cur };
  if (field === 'display') next.display = target;
  else if (field === 'results') {
    next.results = target;
    if (!target) next.before = false; // cascade clear the dependent flag
  } else if (field === 'live') next.before = target;
  // Wire invariant: never persist before-complete without Results.
  next.before = next.results && next.before;
  return next;
}

function toEventPatch(next: ToggleState) {
  return {
    display_prize_to_contestants: next.display,
    show_prize_amounts_in_results: next.results,
    show_prize_amounts_before_complete: next.before,
  } as any;
}

/**
 * Persist one event's toggles. The switch component has already enforced the
 * invariant in the values it hands back, but we re-enforce it on the wire —
 * the component is a UI affordance, not a guarantee.
 */
export function useSaveEventPrizeToggles() {
  const queryClient = useQueryClient();
  const updateEventMutation = useUpdateEvents();

  const saveEventToggles = async (
    eventId: string,
    next: EventPrizeDisplayTogglesValues,
  ) => {
    await updateEventMutation.mutateAsync({
      id: eventId,
      data: toEventPatch({
        display: next.display_prize_to_contestants,
        results: next.show_prize_amounts_in_results,
        before: next.show_prize_amounts_before_complete,
      }),
    });
    queryClient.invalidateQueries({ queryKey: eventsKeys.all });
  };

  return { saveEventToggles, isSaving: updateEventMutation.isPending };
}

/**
 * Bulk-apply one toggle across every event in a rodeo. Only changed events are
 * written — a master switch flipped on a rodeo where 8 of 13 events already
 * match should cost 5 writes, not 13.
 */
export function useApplyRodeoPrizeToggle() {
  const queryClient = useQueryClient();
  const updateEventMutation = useUpdateEvents();
  const [isSaving, setIsSaving] = useState(false);

  const applyRodeoToggle = async (
    events: any[],
    field: PrizeToggleField,
    target: boolean,
  ) => {
    if (!events || events.length === 0) return;
    setIsSaving(true);
    try {
      const writes = events
        .map((ev) => {
          const cur = readToggles(ev);
          const next = applyField(cur, field, target);
          const changed =
            next.display !== cur.display ||
            next.results !== cur.results ||
            next.before !== cur.before;
          return changed ? { id: ev.id as string, next } : null;
        })
        .filter(Boolean) as Array<{ id: string; next: ToggleState }>;

      await Promise.all(
        writes.map((w) =>
          updateEventMutation.mutateAsync({ id: w.id, data: toEventPatch(w.next) }),
        ),
      );
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    } catch {
      // Swallow — same as the publish toggle. The next refetch re-syncs the
      // master switches and per-event rows to DB truth, so a failed write
      // shows up as the switch springing back rather than a stuck lie.
    } finally {
      setIsSaving(false);
    }
  };

  return { applyRodeoToggle, isSaving };
}
